import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit } from '@angular/core';
import { getProcessedTableConfig, PROCESSED_FILES_NO_DATA_MESSAGE } from './processed-table-config';
import { DateUtilsService } from 'src/app/services/date-utils.service';
import { NumberUtilsService } from 'src/app/services/number-utils.service';
import { FileUtilsService } from 'src/app/services/file-utils.service';
import { MatIconModule } from '@angular/material/icon';

import { finalize, Subscription, take } from 'rxjs';
import { PageLoaderService } from 'src/app/services/page-loader.service';
import { GlobalServiceService } from 'src/app/services/global/global-service.service';
import {
  BulkLoadFileService,
  BulkLoadItemDto,
} from 'src/generated-sources/openapi/bulk-load-service/bulk-load-file';
import { TagComponent, TagType } from 'src/app/shared/components/tag/tag.component';
import { SnackbarService } from 'src/app/services/snackbar.service';
import {
  GenericTableComponent,
  SortEvent,
  TableCellDirective,
} from 'src/app/shared/components/generic-table';

enum BulkLoadStatus {
  PROCESSING = 'PROCESSING',
  COMPLETED_WITH_ERROR = 'COMPLETED_WITH_ERROR',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

interface StatusConfig {
  type: TagType;
  text: string;
}

interface PaginationParams {
  page: number;
  pageSize: number;
  sort: string | null;
  dir: 'asc' | 'desc' | null;
}

@Component({
  selector: 'app-processed-tracking-records-files',
  templateUrl: './processed-tracking-records-files.component.html',
  styleUrls: ['./processed-tracking-records-files.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [MatIconModule, TagComponent, GenericTableComponent, TableCellDirective],
  providers: [BulkLoadFileService],
})
export class ProcessedTrackingRecordsFilesComponent implements OnInit, OnDestroy {
  private readonly FILE_EXTENSION = '.xlsx';

  private readonly statusConfig: Record<string, StatusConfig> = {
    [BulkLoadStatus.PROCESSING]: {
      type: 'info',
      text: $localize`:@@inProcessing:En procesamiento`,
    },
    [BulkLoadStatus.COMPLETED_WITH_ERROR]: {
      type: 'warning',
      text: $localize`:@@processedWithErrors:Procesado, con errores`,
    },
    [BulkLoadStatus.ERROR]: {
      type: 'danger',
      text: $localize`:@@processingError:Error de procesamiento`,
    },
    [BulkLoadStatus.SUCCESS]: {
      type: 'success',
      text: $localize`:@@processedWithSuccess:Procesado con éxito`,
    },
  };

  tableConfig = getProcessedTableConfig(this.dateUtilsService, this.numberUtilsService);
  readonly noDataFoundText = PROCESSED_FILES_NO_DATA_MESSAGE;

  processedList: BulkLoadItemDto[] = [];
  isLoading = false;

  readonly params: PaginationParams = {
    page: 0,
    pageSize: 10,
    sort: null,
    dir: null,
  };

  totalRegisters = 0;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly snackBarService: SnackbarService,
    private readonly globalService: GlobalServiceService,
    private readonly bulkLoadService: BulkLoadFileService,
    private readonly pageLoaderService: PageLoaderService,
    private readonly dateUtilsService: DateUtilsService,
    private readonly numberUtilsService: NumberUtilsService,
    private readonly fileUtilsService: FileUtilsService,
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.globalService.getPersonType$().subscribe(() => {
        this.loadMoreData();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  getTagStyle(processedFile: BulkLoadItemDto): TagType {
    const config = this.statusConfig[processedFile.status || BulkLoadStatus.SUCCESS];
    return config?.type || 'success';
  }

  getTagText(processedFile: BulkLoadItemDto): string {
    const config = this.statusConfig[processedFile.status || BulkLoadStatus.SUCCESS];
    return config?.text || this.statusConfig[BulkLoadStatus.SUCCESS].text;
  }

  linkToDownload({ id, fileName }: BulkLoadItemDto): void {
    const position = fileName.indexOf(this.FILE_EXTENSION);

    this.pageLoaderService.setPageLoader(true);
    this.bulkLoadService
      .downloadBulkLoadFileXlsx({ id }, 'response')
      .pipe(
        take(1),
        finalize(() => this.pageLoaderService.setPageLoader(false)),
      )
      .subscribe({
        next: (response) => {
          this.fileUtilsService.downloadBlobFile(response, fileName.substring(0, position));
          this.snackBarService.normal(
            $localize`:@@js_SuccessFileDownload:La descarga del archivo se completó correctamente`,
          );
        },
        error: (error) => {
          console.error('[ProcessedFiles] Error downloading file:', error);
          this.snackBarService.error(
            $localize`:@@js_ErrorFileDownload:Error al intentar descargar el archivo`,
          );
        },
      });
  }

  hasDownloadLink(item: BulkLoadItemDto): boolean {
    return item.status !== BulkLoadStatus.ERROR && item.status !== BulkLoadStatus.PROCESSING;
  }

  changePage(page: number): void {
    this.params.page = page;
    this.loadMoreData();
  }

  loadMoreData(): void {
    const { page, sort, dir, pageSize: perPage } = this.params;
    this.isLoading = true;

    this.bulkLoadService
      .listPageable({ page, perPage, sort, dir })
      .pipe(
        take(1),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: (response) => {
          this.processedList = response.items;
          this.totalRegisters = response.total;
        },
        error: (error) => {
          console.error('[ProcessedFiles] Error loading data:', error);
          this.processedList = [];
          this.snackBarService.error(
            $localize`:@@js_ErrorGettingTrackingRecordProcessedFiles:Error al buscar archivos procesados: `,
          );
        },
      });
  }

  onSortChange(event: SortEvent<BulkLoadItemDto>): void {
    this.params.page = 0;

    if (event.order) {
      this.params.sort = event.sortField;
      this.params.dir = event.order;
    } else {
      this.params.sort = null;
      this.params.dir = null;
    }

    this.loadMoreData();
  }
}
